import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import {contentMaxWidth, colors, fontSize, fontWeight, spacing, borderRadius, transition, typography} from '@/styles/tokens';

// ─── Sub-components ──────────────────────────────────────────────────────────
import PortfolioFilters, {INDUSTRIES, SERVICES} from './PortfolioFilters';
import PortfolioCard from './PortfolioCard';
import Eyebrow from '@/components/Common/Eyebrow';

// ─── Module-level sx constants ────────────────────────────────────────────────
const buttonNoTransformSx = {textTransform: 'none'} as const;

const sectionHeaderSx = (variant: string) => ({
    textAlign: variant === 'homepage' ? 'center' : 'left',
    maxWidth: variant === 'homepage' ? contentMaxWidth.standard : contentMaxWidth.full,
    margin: variant === 'homepage' ? '0 auto' : '0',
    mb: spacing[40]
});

const headingTypoSx = {
    ...typography.sectionHeading,
    mb: spacing[24]
} as const;

const subheadingSx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    lineHeight: 1.75,
    mb: spacing[24]
} as const;

const PROGRESS_BAR_WIDTH = 300;

const progressBarWrapSx = {
    width: PROGRESS_BAR_WIDTH,
    height: spacing[4],
    backgroundColor: colors.neutral.grayMedium,
    margin: `0 auto ${spacing[20]}`,
    borderRadius: borderRadius.full,
    overflow: 'hidden'
} as const;

const progressBarFillSx = {
    height: '100%',
    backgroundColor: colors.primary.deep,
    transition: `width ${transition.slow}`
} as const;
// ─────────────────────────────────────────────────────────────────────────────

// Cards past the visible count: rendered (server HTML) but not displayed.
const hiddenCardsSx = {display: 'none'} as const;

/**
 * LatestPortfolio — orchestrator component.
 *
 * Owns filter + pagination state; delegates rendering to:
 *   - <PortfolioFilters> — filter row + active chips (memo-wrapped)
 *   - <PortfolioCard>    — individual card grid item (memo-wrapped)
 *
 * Because both sub-components are memoized, a filter-state change only
 * re-renders <PortfolioFilters>, not every card.
 */
const LatestPortfolio = (props: any) => {
    const {
        eyebrow,
        heading,
        subheading,
        contentData = [],
        openForm,
        variant = 'portfolio',
        defaultFilters = [],
        showFilters = true,
        initialCount = 6
    } = props;

    // ── Filter state ────────────────────────────────────────────────────────
    const [openFilter, setOpenFilter] = useState<null | 'industry' | 'service'>(null);
    const [selectedFilters, setSelectedFilters] = useState<string[]>(defaultFilters);
    const [visibleCount, setVisibleCount] = useState<number>(initialCount);

    // Reset pagination whenever filters change
    useEffect(() => {
        setVisibleCount(initialCount);
    }, [selectedFilters, initialCount]);

    // ── Stable callbacks passed to PortfolioFilters ─────────────────────────
    const handleToggleFilter = useCallback((type: 'industry' | 'service') => {
        setOpenFilter((prev) => (prev === type ? null : type));
    }, []);

    const handleFilterChange = useCallback((value: string) => {
        setSelectedFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    }, []);

    const handleRemoveFilter = useCallback((value: string) => {
        setSelectedFilters((prev) => prev.filter((v) => v !== value));
    }, []);

    const handleClearAll = useCallback(() => setSelectedFilters([]), []);

    // ── useMemo: only recomputes when filters or source data change ──────────
    const filteredData = useMemo(() => {
        if (selectedFilters.length === 0) return contentData;

        // Separate filters into categories (Industry vs Service)
        // Note: PortfolioFilters exports INDUSTRIES and SERVICES lists
        const selectInd = selectedFilters.filter((f) => INDUSTRIES.some((i: string) => i.toLowerCase() === f.toLowerCase()));
        const selectSrv = selectedFilters.filter((f) => SERVICES.some((s: string) => s.toLowerCase() === f.toLowerCase()));

        return contentData.filter((card: any) => {
            const matchesInd = selectInd.length === 0 || selectInd.some((f) => f.toLowerCase() === card.type?.toLowerCase());

            const matchesSrv =
                selectSrv.length === 0 || card.service?.some((srv: string) => selectSrv.some((f) => f.toLowerCase() === srv.toLowerCase()));

            return matchesInd && matchesSrv;
        });
    }, [selectedFilters, contentData]);

    const progressPct = (visibleCount / filteredData.length) * 100;

    return (
        <Box className="main-wrapper">
            <MUIContainer>
                {/* Heading */}
                <Box sx={sectionHeaderSx(variant)}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                        {heading}
                    </Typography>
                    {subheading && (
                        <Typography className="font-regular-text" sx={subheadingSx}>
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>

                {/* Filters — omitted when the page passes a hand-picked, fixed set of studies */}
                {showFilters && (
                    <PortfolioFilters
                        openFilter={openFilter}
                        selectedFilters={selectedFilters}
                        filteredCount={filteredData.length}
                        variant={variant}
                        onToggleFilter={handleToggleFilter}
                        onFilterChange={handleFilterChange}
                        onRemoveFilter={handleRemoveFilter}
                        onClearAll={handleClearAll}
                    />
                )}

                {/* Card grid */}
                <Grid container spacing={4}>
                    {filteredData.length > 0 ? (
                        filteredData
                            .slice(0, visibleCount)
                            ?.map((item: any, index: number) => (
                                <PortfolioCard key={item.id ?? `card-${index}`} item={item} index={index} openForm={openForm} />
                            ))
                    ) : (
                        <Grid item xs={12}>
                            <Typography textAlign="center" sx={{fontSize: fontSize[20], fontWeight: fontWeight.semibold}}>
                                No Data Found
                            </Typography>
                        </Grid>
                    )}
                </Grid>

                {/* Studies past "Show more" are in the HTML too (search engines read them all) but not displayed. They sit
                    outside the grid: MUI spaces only its direct grid items, so the visible cards stay exactly as before. */}
                {visibleCount < filteredData.length && (
                    <Box sx={hiddenCardsSx}>
                        {filteredData.slice(visibleCount).map((item: any, index: number) => (
                            <PortfolioCard
                                key={item.id ?? `card-${visibleCount + index}`}
                                item={item}
                                index={visibleCount + index}
                                openForm={openForm}
                            />
                        ))}
                    </Box>
                )}

                {/* Load more */}
                {visibleCount < filteredData.length && (
                    <Box mt={5} textAlign="center">
                        {variant === 'homepage' ? (
                            <>
                                <Typography mb={2} fontSize={16}>
                                    You&apos;ve viewed {visibleCount} of {filteredData.length} success stories
                                </Typography>
                                <Box sx={progressBarWrapSx}>
                                    <Box sx={{...progressBarFillSx, width: `${progressPct}%`}} />
                                </Box>
                                <MUIButton
                                    label="Show more success stories"
                                    variant="contained"
                                    size="large"
                                    onClick={() => setVisibleCount((prev) => prev + 6)}
                                    className="button-bg-primary animated-button"
                                    sx={buttonNoTransformSx}
                                />
                            </>
                        ) : (
                            <MUIButton
                                label="Show more success stories"
                                variant="contained"
                                size="large"
                                onClick={() => setVisibleCount((prev) => prev + 6)}
                                className="button-bg-primary animated-button"
                                sx={buttonNoTransformSx}
                            />
                        )}
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(LatestPortfolio);
